import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db, AUTH_EMAIL_SENDER } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const Profile: React.FC = () => {
  const { currentUser, userProfile, resetPassword } = useAuth();
  const navigate = useNavigate();

  // Change password = email myself a reset link. No reauthentication dance: the address
  // is the signed-in account's own, read from the auth session rather than typed, so
  // there is nothing here for a stranger to probe.
  const [pwSending, setPwSending] = useState(false);
  const [pwSent, setPwSent] = useState(false);

  // Email notification toggles. Absent pref => the per-key default (outbid ON, high-bidder
  // OFF/opt-in, proxy-defended ON). Local state reflects the toggle immediately; the write
  // persists to users/{uid} via a dot-path update (only that key changes).
  const [prefs, setPrefs] = useState({
    outbidEmail: userProfile?.notificationPrefs?.outbidEmail ?? true,
    highBidderEmail: userProfile?.notificationPrefs?.highBidderEmail ?? false,
    proxyDefendedEmail: userProfile?.notificationPrefs?.proxyDefendedEmail ?? true,
  });
  const [savingKey, setSavingKey] = useState<string | null>(null);

  if (!currentUser || !userProfile) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Please sign in to view your profile</p>
      </div>
    );
  }

  const isBidder = userProfile.role === 'bidder';

  const togglePref = async (key: keyof typeof prefs, label: string) => {
    if (!currentUser) return;
    const next = !prefs[key];
    setPrefs((p) => ({ ...p, [key]: next })); // optimistic
    setSavingKey(key);
    try {
      // Dot-path update so only this key changes. Rules permit an owner to update their own
      // users doc except authnet/role.
      await updateDoc(doc(db, 'users', currentUser.uid), { [`notificationPrefs.${key}`]: next });
      toast.success(`${label} ${next ? 'on' : 'off'}`);
    } catch (e) {
      setPrefs((p) => ({ ...p, [key]: !next })); // revert on failure
      toast.error('Could not save preference');
      console.error('Failed to update notificationPrefs:', e);
    } finally {
      setSavingKey(null);
    }
  };

  const handlePasswordReset = async () => {
    if (!currentUser?.email) {
      toast.error('This account has no email address on file.');
      return;
    }
    setPwSending(true);
    try {
      await resetPassword(currentUser.email);
      setPwSent(true);
      toast.success('Reset link sent');
    } catch (e) {
      toast.error("Couldn't send the reset email — try again in a moment.");
      console.error('Password reset failed:', e);
    } finally {
      setPwSending(false);
    }
  };

  const PrefToggle: React.FC<{ prefKey: keyof typeof prefs; title: string; desc: string }> = ({
    prefKey,
    title,
    desc,
  }) => (
    <div className="flex items-center justify-between py-2">
      <div className="pr-4">
        <p className="font-semibold">{title}</p>
        <p className="text-sm text-gray-600">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={prefs[prefKey]}
        onClick={() => togglePref(prefKey, title)}
        disabled={savingKey === prefKey}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
          prefs[prefKey] ? 'bg-primary-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            prefs[prefKey] ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <h1 className="text-3xl font-bold">My Profile</h1>

      {/* Account Information */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">Account Information</h2>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-semibold">{currentUser.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Role</p>
              <p className="font-semibold capitalize">{userProfile.role}</p>
            </div>
          </div>

          {isBidder && userProfile.bidderNumber && (
            <div>
              <p className="text-sm text-gray-600">Bidder Number</p>
              <p className="font-bold text-2xl text-primary-600 font-mono">
                #{userProfile.bidderNumber}
              </p>
            </div>
          )}

          {userProfile.displayName && (
            <div>
              <p className="text-sm text-gray-600">Name</p>
              <p className="font-semibold">{userProfile.displayName}</p>
            </div>
          )}

          {userProfile.phoneNumber && (
            <div>
              <p className="text-sm text-gray-600">Phone</p>
              <p className="font-semibold">{userProfile.phoneNumber}</p>
            </div>
          )}
        </div>
      </div>

      {/* Billing Address */}
      {isBidder && (userProfile.addressLine1 || userProfile.city) && (
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Billing Address</h2>
          <div className="space-y-2">
            {userProfile.addressLine1 && <p>{userProfile.addressLine1}</p>}
            {userProfile.addressLine2 && <p>{userProfile.addressLine2}</p>}
            {userProfile.city && (
              <p>
                {userProfile.city}, {userProfile.state} {userProfile.zipCode}
              </p>
            )}
            {userProfile.country && <p>{userProfile.country}</p>}
          </div>
        </div>
      )}

      {/* Payment authorization now happens through the sale registration flow
          (Track B $0 card auth), not here. The old fake "Authorize (Test)" write
          was removed — it set cardAuthorized without any real authorization. */}

      {/* Password */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">Password</h2>
        {pwSent ? (
          <div>
            <p className="text-sm font-semibold text-green-800">✓ Reset link sent</p>
            <p className="text-sm text-gray-700 mt-1">
              Check <span className="font-semibold break-all">{currentUser.email}</span> for a link
              to set a new password.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              It comes from <span className="font-mono break-all">{AUTH_EMAIL_SENDER}</span> — look
              in your spam folder if it hasn't arrived in a minute.
            </p>
            <button
              type="button"
              onClick={() => setPwSent(false)}
              className="text-sm text-gray-500 hover:text-gray-700 underline mt-3"
            >
              Send another
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="pr-4">
              <p className="font-semibold">Change your password</p>
              <p className="text-sm text-gray-600">
                We'll email a secure link to{' '}
                <span className="font-semibold break-all">{currentUser.email}</span> so you can set
                a new one.
              </p>
            </div>
            <button
              type="button"
              onClick={handlePasswordReset}
              disabled={pwSending}
              className="btn-secondary text-sm whitespace-nowrap disabled:opacity-50"
            >
              {pwSending ? 'Sending…' : 'Email me a reset link'}
            </button>
          </div>
        )}
      </div>

      {/* Email Notifications */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">Email Notifications</h2>
        <div className="divide-y divide-gray-100">
          <PrefToggle
            prefKey="outbidEmail"
            title="Outbid alerts"
            desc="Email me when someone outbids me on a lot."
          />
          <PrefToggle
            prefKey="proxyDefendedEmail"
            title="Auto-bid defended"
            desc="Email me when my auto-bid holds my lead against a challenge."
          />
          <PrefToggle
            prefKey="highBidderEmail"
            title="High-bidder confirmations"
            desc="Email me every time I become the high bidder (can be frequent)."
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={() => navigate('/browse')}
          className="btn-secondary flex-1"
        >
          Back to Auctions
        </button>
        {!userProfile.addressLine1 && isBidder && (
          <button
            onClick={() => navigate('/register-bidder')}
            className="btn-primary flex-1"
          >
            Complete Profile
          </button>
        )}
      </div>
    </div>
  );
};

export default Profile;